import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContextWithQuery";
import { InviteUserForm } from "../../components/forms/InviteUserForm";
import {
  useTSMDeactivateBotToken,
  useTSMReactivateBotToken,
  useTSQFetchBotList,
} from "../../hooks/useTSBots";
import { BotTokenResponse } from "../../types/botTypes";
import { CreateBotForm } from "../../components/forms/CreateBotTokenForm";

export const PageBotList: React.FC = () => {
  const navigate = useNavigate();

  const [error, setError] = useState<string | null>(null);

  const [createModalOpen, setCreateModalOpen] = useState(false);

  const { user } = useAuth();

  const thisUser = user;

  const {
    data: botsResponse,
    isLoading: botsLoading,
    isFetching: botsFetching,
    error: botsError,
  } = useTSQFetchBotList(undefined, undefined, false, true);

  const [allBots, botCount]: [BotTokenResponse[], number] = useMemo(() => {
    if (botsResponse) {
      return [botsResponse.tokens, botsResponse.total];
    }
    return [[], 0];
  }, [botsResponse]);

  const deactivateBotMutation = useTSMDeactivateBotToken();
  const reactivateBotMutation = useTSMReactivateBotToken();

  const handleReactivate = async (id: string) => {
    if (!window.confirm("Are you sure you want to reactivate this Bot?")) {
      return;
    }

    try {
      await reactivateBotMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to reactivate Bot:", err);
      setError(err.response?.data?.detail || "Failed to reactivate Bot");
    }
  };

  const handleDeactivate = async (id: string) => {
    if (!window.confirm("Are you sure you want to deactivate this Bot?")) {
      return;
    }

    try {
      await deactivateBotMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to deactivate Bot:", err);
      setError(err.response?.data?.detail || "Failed to deactivate Bot");
    }
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Bot Tokens
        </Typography>
        <Stack direction="row" spacing={2}>
          {/* Show Invite User button only for superusers */}
          {thisUser?.is_superuser && (
            <Button
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={() => setCreateModalOpen(true)}
            >
              Add New Bot Token
            </Button>
          )}
        </Stack>
      </Stack>

      {(error || botsError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || botsError?.message || "An unknown error occurred"}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Permissions</TableCell>
              <TableCell>Dates</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {botsLoading && allBots.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : allBots.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    No Bot Tokens found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              allBots.map((bot) => (
                <TableRow key={bot.id} hover>
                  <TableCell>{bot.name}</TableCell>
                  <TableCell>{bot.description}</TableCell>
                  <TableCell>{bot.permissions.join(", ")}</TableCell>
                  <TableCell>
                    <Stack direction="column" spacing={0.5}>
                      <Typography variant="body1">
                        Last Used:{" "}
                        {bot.last_used_at
                          ? new Date(bot.last_used_at).toLocaleDateString(
                              "en-US",
                            )
                          : "N/A"}
                      </Typography>
                      <Typography variant="body1">
                        Created:{" "}
                        {new Date(bot.created_at).toLocaleDateString("en-US")}
                      </Typography>
                      <Typography variant="body1">
                        Updated:{" "}
                        {new Date(bot.updated_at).toLocaleDateString("en-US")}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {!bot.is_active ? (
                        <Tooltip title="Reactivate Bot">
                          <IconButton
                            size="small"
                            onClick={() => handleReactivate(bot.id)}
                            color="success"
                            disabled={reactivateBotMutation.isPending}
                          >
                            <RestoreIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Deactivate Bot">
                          <IconButton
                            size="small"
                            onClick={() => handleDeactivate(bot.id)}
                            color="error"
                            disabled={deactivateBotMutation.isPending}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Invite User Modal */}
      <CreateBotForm
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={() => {
          // Refresh user list when invitation is successfully sent
          // The user list will update automatically via TanStack Query
        }}
      />
    </Box>
  );
};
